import {
  CONTEXT_KEY,
  type ContextValues,
  type EntityId,
} from "@atoms/atom-types";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { useDebugSettings } from "./hooks/useDebugSettings";
import { ExtraContextProvider, useExtraContext } from "./hooks/useExtraContext";

/**
 * SetIdentifier component for providing entity context to children.
 * @param identifier - The entity name (e.g., Entities.Position, DefaultEntities.User)
 * @param value - The entity ID value
 * @param children - Child components that will receive the context
 */
export const SetIdentifier = <T extends string = string>({
  identifier,
  value,
  children,
}: {
  identifier: T;
  value: EntityId<T> | string | undefined;
  children: ReactNode;
}) => {
  const parentContext = useExtraContext();
  const { enableContextPicker } = useDebugSettings();
  // Add state to force re-render when enableContextPicker changes
  const [, setForceUpdate] = useState({});

  // Use effect with dependency on enableContextPicker to trigger re-renders
  useEffect(() => {
    setForceUpdate({});
  }, []);

  // Use CONTEXT_KEY to create the proper key format (e.g., "<Position>")
  const contextKey = CONTEXT_KEY(identifier);
  const contextValue = useMemo(
    () => ({ ...parentContext, [contextKey]: value }),
    [parentContext, contextKey, value],
  );
  const childWithContextMenu = addContextMenuToChildren(
    children,
    enableContextPicker,
    contextValue,
  );

  return (
    <ExtraContextProvider value={contextValue}>
      {childWithContextMenu}
    </ExtraContextProvider>
  );
};

export const SetExtraContext = ({
  context,
  children,
}: {
  context: ContextValues;
  children: ReactNode;
}) => {
  const parentContext = useExtraContext();
  const { enableContextPicker } = useDebugSettings();
  // Add state to force re-render when enableContextPicker changes
  const [, setForceUpdate] = useState({});

  // Use effect with dependency on enableContextPicker to trigger re-renders
  useEffect(() => {
    setForceUpdate({});
  }, []);

  const contextValue = useMemo(
    () => ({ ...parentContext, ...context }),
    [parentContext, context],
  );
  const childWithContextMenu = addContextMenuToChildren(
    children,
    enableContextPicker,
    contextValue,
  );

  return (
    <ExtraContextProvider value={contextValue}>
      {childWithContextMenu}
    </ExtraContextProvider>
  );
};

// Helper function to add context menu functionality to children
const addContextMenuToChildren = (
  children: ReactNode,
  enableContextPicker: boolean,
  contextValue: ContextValues,
) => {
  return React.Children.map(children, (child, index) => {
    // Only add the context menu to the first child
    if (
      index === 0 &&
      React.isValidElement<{
        onContextMenu?: (e: React.MouseEvent) => void;
        style?: React.CSSProperties;
      }>(child)
    ) {
      const childWithMenu = React.cloneElement(child, {
        onContextMenu: (e: React.MouseEvent) => {
          // Call the original onContextMenu if it exists
          if (child.props.onContextMenu) {
            child.props.onContextMenu(e);
          }

          if (enableContextPicker) {
            navigator.clipboard.writeText(JSON.stringify(contextValue));

            // Display notification that context has been copied to clipboard
            const notification = document.createElement("div");
            notification.textContent = "Context copied to clipboard";
            notification.style.position = "fixed";
            notification.style.top = "20px";
            notification.style.left = "50%";
            notification.style.transform = "translateX(-50%)";
            notification.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
            notification.style.color = "white";
            notification.style.padding = "10px 20px";
            notification.style.borderRadius = "4px";
            notification.style.zIndex = "1000";
            document.body.appendChild(notification);

            // Remove notification after 3 seconds
            setTimeout(() => {
              document.body.removeChild(notification);
            }, 3000);
          }
        },
        style: {
          ...child.props.style,
          border: enableContextPicker ? "2px solid red" : undefined,
        },
      });
      return childWithMenu;
    }
    return child;
  });
};
